import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowRight } from 'lucide-react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useFormTracking } from '../../hooks/useAnalytics';

/**
 * Enhanced Welcome Step with comprehensive analytics tracking
 * Step 1: Personal Information (User Name, Job Title, Referral Source)
 */
const WelcomeStep = ({ data, updateData, onNext, userName }) => {
  const { 
    trackStepStart, 
    trackStepCompleted, 
    trackFieldInteraction,
    trackValidationFailure,
    trackSpecialFieldSelection 
  } = useOnboarding();
  
  const { 
    trackFormStart,
    trackFieldFocus, 
    trackFieldChange,
    trackFormSubmit 
  } = useFormTracking('onboarding_step_1_personal');
  
  const [formData, setFormData] = useState({
    userName: data.userName || '',
    jobTitle: data.jobTitle || '',
    hearAboutUs: data.hearAboutUs || '',
    hearAboutUsOther: data.hearAboutUsOther || ''
  });
  
  const [errors, setErrors] = useState({});
  const [showOtherInput, setShowOtherInput] = useState(false);
  const fieldRefs = useRef({});
  const stepStartTime = useRef(Date.now());
  
  // Referral source options
  const hearAboutUsOptions = [
    'Search Engine',
    'Social Media',
    'LinkedIn',
    'Friend/Colleague',
    'Advertisement',
    'Blog/Article',
    'Event/Conference',
    'Other'
  ];
  
  // Track step start on mount
  useEffect(() => {
    trackStepStart(1, 'personal_info');
    trackFormStart();
  }, [trackStepStart, trackFormStart]);
  
  /**
   * Handle input changes with analytics
   */
  const handleInputChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    updateData(newFormData);
    
    // Track field interaction
    trackFieldInteraction(1, field, 'change', value);
    trackFieldChange(field, value);
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Handle special "Other" selection
    if (field === 'hearAboutUs') {
      const isOther = value === 'Other';
      setShowOtherInput(isOther);
      
      if (!isOther) {
        // Clear other field if not selected
        const updatedData = { ...newFormData, hearAboutUsOther: '' };
        setFormData(updatedData);
        updateData(updatedData);
      }
      
      // Track special selection
      trackSpecialFieldSelection('hearAboutUs', value, isOther);
    }
  };
  
  /**
   * Handle field focus with analytics
   */
  const handleFieldFocus = (field) => {
    trackFieldInteraction(1, field, 'focus');
    trackFieldFocus(field);
  };
  
  /**
   * Handle field blur with analytics
   */
  const handleFieldBlur = (field, value) => {
    trackFieldInteraction(1, field, 'blur', value);
    
    // Track specific events for key fields
    if (field === 'userName' && value) {
      trackFieldInteraction(1, 'user_name_entered', 'completed', value);
    } else if (field === 'jobTitle' && value) {
      trackFieldInteraction(1, 'job_title_entered', 'completed', value);
    }
  };
  
  /**
   * Validate form with analytics tracking
   */
  const validateForm = () => {
    const newErrors = {};
    let validationFailures = [];
    
    // Validate user name
    if (!formData.userName.trim()) {
      newErrors.userName = 'Your name is required';
      validationFailures.push('userName');
    } else if (formData.userName.trim().length < 2) {
      newErrors.userName = 'Please enter a valid name';
      validationFailures.push('userName');
    }
    
    // Validate job title
    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
      validationFailures.push('jobTitle');
    } else if (formData.jobTitle.trim().length < 2) {
      newErrors.jobTitle = 'Please enter a valid job title';
      validationFailures.push('jobTitle');
    }
    
    // Validate referral source
    if (!formData.hearAboutUs) {
      newErrors.hearAboutUs = 'Please select how you heard about us';
      validationFailures.push('hearAboutUs');
    }
    
    // Validate "Other" input if selected
    if (formData.hearAboutUs === 'Other' && !formData.hearAboutUsOther.trim()) {
      newErrors.hearAboutUsOther = 'Please specify how you heard about us';
      validationFailures.push('hearAboutUsOther');
    }
    
    // Track validation failures
    validationFailures.forEach(field => {
      trackValidationFailure(1, field, newErrors[field]);
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * Handle next button click
   */
  const handleNext = () => {
    if (validateForm()) {
      const timeSpent = Math.round((Date.now() - stepStartTime.current) / 1000);
      
      // Track step completion
      trackStepCompleted(1, 'personal_info');
      
      // Track form submission
      trackFormSubmit(true);
      
      // Track collected data
      const fieldsCompleted = [
        formData.userName ? 'userName' : null,
        formData.jobTitle ? 'jobTitle' : null,
        formData.hearAboutUs ? 'hearAboutUs' : null,
        formData.hearAboutUsOther ? 'hearAboutUsOther' : null
      ].filter(Boolean);
      
      trackFieldInteraction(1, 'step_completed', 'submit', {
        fields_completed: fieldsCompleted,
        time_spent: timeSpent
      });
      
      onNext();
    } else {
      // Track form submission failure
      trackFormSubmit(false, Object.keys(errors));
    }
  };
  
  /**
   * Handle Enter key press
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleNext();
    }
  };
  
  return (
    <div className="flex flex-col gap-6">
      <div className="text-center space-y-2">
        <p className="text-sm text-muted-foreground">
          Let's start by learning a bit more about you and your role.
        </p>
      </div>
      
      <div className="flex flex-col gap-6">
        {/* User Name Field */}
        <div className="space-y-2">
          <Label htmlFor="userName" className="text-card-foreground">
            What's your name? *
          </Label>
          <Input
            id="userName"
            ref={el => fieldRefs.current.userName = el}
            type="text"
            placeholder="Enter your full name"
            value={formData.userName}
            onChange={(e) => handleInputChange('userName', e.target.value)}
            onFocus={() => handleFieldFocus('userName')}
            onBlur={(e) => handleFieldBlur('userName', e.target.value)}
            onKeyPress={handleKeyPress}
            className={errors.userName ? 'border-red-500' : ''}
            autoComplete="name"
          />
          {errors.userName && (
            <p className="text-sm text-red-600">{errors.userName}</p>
          )}
        </div>
        
        {/* Job Title Field */}
        <div className="space-y-2">
          <Label htmlFor="jobTitle" className="text-card-foreground">
            What's your job title? *
          </Label>
          <Input
            id="jobTitle"
            ref={el => fieldRefs.current.jobTitle = el}
            type="text"
            placeholder="e.g., Marketing Manager, CEO, Sales Representative"
            value={formData.jobTitle}
            onChange={(e) => handleInputChange('jobTitle', e.target.value)}
            onFocus={() => handleFieldFocus('jobTitle')}
            onBlur={(e) => handleFieldBlur('jobTitle', e.target.value)}
            onKeyPress={handleKeyPress}
            className={errors.jobTitle ? 'border-red-500' : ''}
            autoComplete="organization-title"
          />
          {errors.jobTitle && (
            <p className="text-sm text-red-600">{errors.jobTitle}</p>
          )}
        </div>
        
        {/* How You Heard About Us */}
        <div className="space-y-2">
          <Label htmlFor="hearAboutUs" className="text-card-foreground">
            How did you hear about 44Agents? *
          </Label>
          <Select
            value={formData.hearAboutUs}
            onValueChange={(value) => handleInputChange('hearAboutUs', value)}
            onOpenChange={(open) => {
              if (open) handleFieldFocus('hearAboutUs');
              else handleFieldBlur('hearAboutUs', formData.hearAboutUs);
            }}
          >
            <SelectTrigger 
              id="hearAboutUs"
              className={errors.hearAboutUs ? 'border-red-500' : ''}
            >
              <SelectValue placeholder="Please select..." />
            </SelectTrigger>
            <SelectContent>
              {hearAboutUsOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.hearAboutUs && (
            <p className="text-sm text-red-600">{errors.hearAboutUs}</p>
          )}
        </div>
        
        {/* Other Input (Conditional) */}
        {showOtherInput && (
          <div className="space-y-2">
            <Label htmlFor="hearAboutUsOther" className="text-card-foreground">
              Please specify how you heard about us *
            </Label>
            <Input
              id="hearAboutUsOther"
              ref={el => fieldRefs.current.hearAboutUsOther = el}
              type="text"
              placeholder="Please tell us more..."
              value={formData.hearAboutUsOther}
              onChange={(e) => handleInputChange('hearAboutUsOther', e.target.value)}
              onFocus={() => handleFieldFocus('hearAboutUsOther')}
              onBlur={(e) => handleFieldBlur('hearAboutUsOther', e.target.value)}
              onKeyPress={handleKeyPress}
              className={errors.hearAboutUsOther ? 'border-red-500' : ''}
            />
            {errors.hearAboutUsOther && (
              <p className="text-sm text-red-600">{errors.hearAboutUsOther}</p>
            )}
          </div>
        )}
      </div>
      
      {/* Next Button */}
      <div className="flex justify-end mt-6">
        <Button 
          onClick={handleNext} 
          className="gap-2"
          type="button"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default WelcomeStep;